import classnames from 'classnames';
import { type FunctionComponent } from 'react';

import { Spacing } from '@/core/tamagoshi/Spacing/Spacing';
import { Text, type TextType } from '@/core/tamagoshi/Text/Text';

import styles from './Ratings.module.scss';

const SIZE_TO_FONTSIZE = {
  lg: '32px',
  md: '24px',
  none: undefined,
  sm: undefined,
} as const satisfies Record<
  NonNullable<RatingsProps['size']>,
  string | undefined
>;

const SIZE_TO_TYPE = {
  lg: 'h3',
  md: 'body1',
  none: undefined,
  sm: 'small',
} as const satisfies Record<
  NonNullable<RatingsProps['size']>,
  TextType | undefined
>;

interface RatingsProps {
  className?: string;
  starsClassName?: string;
  count?: string | number;
  nbStars: number;
  size?: 'sm' | 'md' | 'lg' | 'none';
  theme?: 'outline' | 'fill';
}

/**
 * Ratings component display the product rating and the number of reviews.
 * @deprecated Use `@/core/tamagoshiv2/components/Rating` instead.
 */
export const Ratings: FunctionComponent<RatingsProps> = ({
  className,
  starsClassName,
  count,
  nbStars,
  size = 'md',
  theme = 'outline',
}) => {
  const rootClassNames = classnames(styles.root, className);
  const starsClassNames = classnames(styles.stars, starsClassName, {
    [styles.fill]: theme === 'fill',
  });
  const offset = Math.max(0, Math.min(100, 10 * Math.round(nbStars * 2)));
  return nbStars > 0 || theme === 'fill' ? (
    <div className={rootClassNames}>
      <span
        aria-label={`${nbStars} out of 5`}
        className={starsClassNames}
        role="img"
        style={{
          backgroundPositionY: `${offset}%`,
          fontSize: SIZE_TO_FONTSIZE[size],
        }}
      />
      {!!count && (
        <Spacing type="ml" size={4}>
          <Text
            type={SIZE_TO_TYPE[size]}
            className={styles.count}
            color="#505971"
          >
            {count}
          </Text>
        </Spacing>
      )}
    </div>
  ) : null;
};

Ratings.displayName = 'Ratings';
